import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import type { ReactNode } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import landingPageI18n, {
  landingPageSupportedLanguages,
  type LandingPageLanguage,
} from "@/i18n/landingPage";
import type { Language } from "@/types/sites";

interface LandingPageLanguageContextType {
  currentLanguage: Language;
  changeLanguage: (language: Language) => void;
  supportedLanguages: LandingPageLanguage[];
  currentLangConfig: LandingPageLanguage | undefined;
  isLoading: boolean;
}

const LandingPageLanguageContext = createContext<
  LandingPageLanguageContextType | undefined
>(undefined);

interface LandingPageLanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

/**
 * Language provider specifically for the landing page
 * This is completely isolated from the main app's language system
 */
export function LandingPageLanguageProvider({
  children,
  defaultLanguage = "en",
}: LandingPageLanguageProviderProps) {
  const [currentLanguage, setCurrentLanguage] = useState(defaultLanguage);
  const [isLoading, setIsLoading] = useState(true);

  const currentLangConfig = landingPageSupportedLanguages.find(
    (lang) => lang.code === currentLanguage
  );
  const changeLanguage = useCallback(async (language: Language) => {
    if (landingPageSupportedLanguages.some((lang) => lang.code === language)) {
      setIsLoading(true);
      await landingPageI18n.changeLanguage(language);
      setCurrentLanguage(language);
      setIsLoading(false);
    }
  }, []);
  // Update language when defaultLanguage prop changes
  useEffect(() => {
    if (defaultLanguage && defaultLanguage !== currentLanguage) {
      setCurrentLanguage(defaultLanguage);
    }
  }, [defaultLanguage]); // currentLanguage excluded to prevent infinite loop

  // Initialize with current language
  useEffect(() => {
    const initializeLanguage = async () => {
      setIsLoading(true);
      await landingPageI18n.changeLanguage(currentLanguage);
      setIsLoading(false);
    };

    initializeLanguage();
  }, [currentLanguage]);
  const contextValue: LandingPageLanguageContextType = {
    currentLanguage,
    changeLanguage,
    supportedLanguages: landingPageSupportedLanguages,
    currentLangConfig,
    isLoading,
  };
  return (
    <LandingPageLanguageContext.Provider value={contextValue}>
      <I18nextProvider i18n={landingPageI18n}>{children}</I18nextProvider>
    </LandingPageLanguageContext.Provider>
  );
}

/**
 * Hook to use the landing page language context
 * Provides fallback values during HMR to prevent crashes when providers
 * temporarily unmount
 */
export function useLandingPageLanguage() {
  const context = useContext(LandingPageLanguageContext);
  if (context === undefined) {
    // Provide a fallback for cases where the hook is used outside the provider
    // (e.g., during HMR when providers may temporarily be in an inconsistent state)
    return {
      currentLanguage: "en" as Language,
      changeLanguage: () => {},
      supportedLanguages: landingPageSupportedLanguages,
      currentLangConfig: landingPageSupportedLanguages.find(
        (lang) => lang.code === "en"
      ),
      isLoading: false,
    };
  }
  return context;
}

/**
 * Hook to use landing page translations
 * This is a wrapper around useTranslation that works with the landing page i18n instance
 */
export function useLandingPageTranslation() {
  const { t, i18n } = useTranslation();
  return { t, i18n };
}

/**
 * Component that ensures language is loaded before rendering children
 */
interface LandingPageLanguageLoaderProps {
  children: ReactNode;
}
export function LandingPageLanguageLoader({
  children,
}: LandingPageLanguageLoaderProps) {
  const { isLoading } = useLandingPageLanguage();

  // Don't show local loader - AppLoader handles all loading states
  if (isLoading) {
    return null;
  }

  return <>{children}</>;
}
